'use client'

import { useDevice } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Image, { StaticImageData } from 'next/image'
import { ReactNode, useMemo } from 'react'

import OverflowImage from '@/components/TextWithImageGoingOffScreen/OverflowImage'
import TitleAndParagraph from '@/components/TitleAndParagraph'
import TwoColumnLayout from '@/components/TwoColumnLayout'

export enum ColumnWrapOrder {
    REVERSE = 'reverse',
    NORMAL = 'normal',
}

/**
 *  TextWithImageGoingOffScreen needs to support 2 different image heights and they require different width/
 *  number for each device size to avoid warping the image.
 */
export enum BaseImageHeight {
    LARGE = 1440,
    MEDIUM = 1280,
}

const TextWithImageGoingOffScreen = ({
    img,
    title,
    paragraph,
    snippet,
    reverse,
    button,
    imgAlt,
}: {
    img: StaticImageData
    title: string
    paragraph: ReactNode
    snippet?: ReactNode
    button?: ReactNode
    reverse?: boolean
    imgAlt?: string
}) => {
    const devices = useDevice()

    const containerHeight = useMemo(() => {
        const { mdScreen, smScreen, xsScreen } = devices
        const isLargeImage = img.height === BaseImageHeight.LARGE
        if (xsScreen) {
            return isLargeImage ? 558 : 496
        }

        if (smScreen) {
            return isLargeImage ? 305 : 289
        }

        if (mdScreen) {
            return isLargeImage ? 477 : 424
        }

        return isLargeImage ? 720 : 640
    }, [img.height, devices])

    const imageDimensions = useMemo(() => {
        const { mdScreen, smScreen, xsScreen } = devices
        const isLargeImage = img.height === BaseImageHeight.LARGE
        if (xsScreen) {
            return {
                width: 670,
                height: isLargeImage ? 558 : 496,
            }
        }

        if (smScreen) {
            return {
                width: 366,
                height: isLargeImage ? 305 : 271,
            }
        }

        if (mdScreen) {
            return {
                width: 572,
                height: isLargeImage ? 477 : 424,
            }
        }

        return {
            width: 864,
            height: isLargeImage ? 720 : 640,
        }
    }, [devices, img.height])

    return (
        <Box
            sx={{
                position: 'relative',
            }}
        >
            <TwoColumnLayout
                leftCol={6}
                rightCol={5}
                columnWrapOrderOnMobile={ColumnWrapOrder.NORMAL}
                sx={{
                    flexDirection: reverse ? 'row-reverse' : 'row',
                }}
            >
                <TitleAndParagraph
                    title={title}
                    paragraph={paragraph}
                    snippet={snippet}
                    button={button}
                />
                <Box
                    sx={{
                        height: containerHeight,
                        mt: `-1px`,
                        display: `flex`,
                        flexDirection: `column`,
                        justifyContent: `center`,
                    }}
                >
                    <OverflowImage float={reverse ? 'right' : 'left'}>
                        <Image
                            width={imageDimensions.width}
                            height={imageDimensions.height}
                            sizes={`${imageDimensions.width}px`}
                            src={img}
                            alt={imgAlt ?? 'Lune image'}
                        />
                    </OverflowImage>
                </Box>
            </TwoColumnLayout>
        </Box>
    )
}

export default TextWithImageGoingOffScreen
